import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { PlusOutlined } from "us.icons";
import * as Actions from "us.collection.admin/actions";
import { ClosureCodesActions } from "us.collection.admin/constants";
import {
  ClosureCodesTable,
  ClosureCodesDetails,
  ClosureCodeHistory,
} from "./Components";
import {
  $Affix,
  $PageHeader,
  $Divider,
  $Button,
  $Drawer,
} from "us.common/components";
import { getSelectedContent } from "./Functions";
import { ClosureCodeDrawerInitialDetails } from "./Constants";
import { useSafeBackNavigation } from "us.helper/navigation";

/**
 * @description - Manage Closure Code home view component to manage closure code add / edit
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 11/07/2024
 */
const ClosureCodesHome: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const goBack = useSafeBackNavigation();
  const { closureCodeList, getClosureCodes, drawerDetails, openDrawer } = props;

  const { isVisible, action, codeId } = drawerDetails ?? {};

  useEffect(() => {
    getClosureCodes && getClosureCodes({});
  }, []);

  /**
   * @description - Handle the add/edit/history view drawer data
   * @param action - Drawer action (add/edit/log)
   * @returns
   */
  const getDrawerTitle = (action: string): string => {
    switch (action) {
      case ClosureCodesActions.NEW:
        return t(
          "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.ADD_NEW_CLOSURE_CODE",
        );
        break;
      case ClosureCodesActions.EDIT:
        return t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.EDIT_CLOSURE_CODE");
        break;
      case ClosureCodesActions.DELETE:
        return t(
          "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DELETE_CLOSURE_CODE",
        );
        break;
      case ClosureCodesActions.LOG:
        return `${t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.CHANGE_LOG")} : ${
          getSelectedContent(closureCodeList?.data, codeId)?.name
        }`;
        break;
      default:
        return "";
        break;
    }
  };

  return (
    <>
      <$Affix offsetTop={48}>
        <div className="page-header header-border">
          <div className="d-flex align-items-center">
            <$PageHeader
              className="px-0"
              onBack={goBack}
              title={t(
                "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.CLOSURE_CODE_SETUP",
              )}
            />
            <$Divider className="bui-devider" type="vertical" />
            <$Button
              className="mr-2"
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                openDrawer &&
                openDrawer({
                  isVisible: true,
                  codeId: -1,
                  action: ClosureCodesActions.NEW,
                })
              }
            >
              {t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.NEW_CLOSURE_CODE")}
            </$Button>
          </div>
        </div>
      </$Affix>

      <div className="space-content manage-closure-codes">
        <ClosureCodesTable />

        <$Drawer
          title={getDrawerTitle(action)}
          width={action == ClosureCodesActions.LOG ? "1000px" : "450px"}
          open={isVisible}
          onClose={() =>
            openDrawer && openDrawer(ClosureCodeDrawerInitialDetails)
          }
          destroyOnClose={true}
          maskClosable={false}
        >
          {action == ClosureCodesActions.LOG && (
            <ClosureCodeHistory
              onCancel={() =>
                openDrawer && openDrawer(ClosureCodeDrawerInitialDetails)
              }
            />
          )}

          {(action == ClosureCodesActions.NEW ||
            action == ClosureCodesActions.EDIT) && (
            <ClosureCodesDetails
              onCancel={() =>
                openDrawer && openDrawer(ClosureCodeDrawerInitialDetails)
              }
            />
          )}
        </$Drawer>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { closureCodes } = state;
  const { closureCodeList, drawerDetails } = closureCodes;

  return {
    closureCodeList,
    drawerDetails,
  };
};

const { closureCode, newClosureCode } = Actions.closureCodes;

const mapDispatchToProps = {
  getClosureCodes: closureCode.get,
  openDrawer: newClosureCode.openDrawer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClosureCodesHome);
